<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1100"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="d-flex flex-column" height="80vh" max-height="90vh">
      <v-card-title class="d-flex align-center justify-space-between pa-4 flex-shrink-0">
        <span>Workflow Specification</span>
        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="flex-shrink-0" />

      <v-card-text class="pa-4 flex-grow-1 d-flex flex-column" style="min-height: 0;">
        <div class="d-flex align-center ga-3 mb-3 flex-shrink-0 flex-wrap">
          <v-select
            v-if="boundedContextOptions.length > 0"
            :model-value="selectedBoundedContextId"
            :items="boundedContextOptions"
            item-title="title"
            item-value="value"
            label="Bounded context"
            variant="outlined"
            density="compact"
            hide-details
            style="min-width: 240px; max-width: 360px;"
            @update:model-value="selectedBoundedContextId = $event"
          />
          <v-spacer />
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-content-copy"
            :disabled="!specJson"
            @click="copySpec"
          >
            Copy
          </v-btn>
          <v-btn
            variant="tonal"
            size="small"
            prepend-icon="mdi-download"
            :disabled="!specJson"
            @click="downloadSpec"
          >
            Download
          </v-btn>
        </div>

        <div class="flex-grow-1" style="min-height: 0;">
          <code-editor
            :modelValue="specJson"
            :isDirty="false"
            :readOnly="true"
            language="json"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { downloadFile } from '@/utils/fsUtils'
import { sanitizeFilename } from '@/utils/sanitize'
import { buildWorkflowSpec } from '@/utils/workflowExport'
import CodeEditor from '@/components/CodeEditor.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:modelValue'])

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const cardTypesMap = computed(() => {
  const cardTypes = store.state.cardTypes?.cardTypes || []

  return Object.fromEntries(cardTypes.map((ct) => [ct.id, ct]))
})

const boundedContextOptions = computed(() => {
  const boundedContexts = workflow.value?.boundedContexts || []

  return boundedContexts.map((bc) => ({ title: bc.name || 'Unnamed', value: bc.id }))
})

const selectedBoundedContextId = ref(null)

const selectedBoundedContext = computed(() => {
  const boundedContexts = workflow.value?.boundedContexts || []

  return boundedContexts.find((bc) => bc.id === selectedBoundedContextId.value) || boundedContexts[0] || null
})

const specJson = computed(() => {
  if (!workflow.value) {
    return ''
  }
  const raw = buildWorkflowSpec(workflow.value, selectedBoundedContext.value, cardTypesMap.value, true)
  if (!raw) {
    return ''
  }
  // buildWorkflowSpec returns a JSON string — reformat with 2-space indent for readability
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
})

const copySpec = async () => {
  if (!specJson.value) {
    return
  }
  try {
    await navigator.clipboard.writeText(specJson.value)
    store.commit('snackbar/showMessage', {
      content: 'Copied specification to clipboard',
      color: 'green',
      timeout: 1500
    }, { root: true })
  } catch {
    store.commit('snackbar/showMessage', {
      content: 'Failed to copy specification',
      color: 'red',
      timeout: 3000
    }, { root: true })
  }
}

const downloadSpec = () => {
  if (!specJson.value) {
    return
  }
  const workflowName = workflow.value?.name || 'workflow'
  downloadFile(sanitizeFilename(workflowName, 'json'), 'application/json', specJson.value)
}

// On open, default the selection to the first bounded context (matches MCP behaviour)
watch(() => props.modelValue, (open) => {
  if (open) {
    const boundedContexts = workflow.value?.boundedContexts || []
    selectedBoundedContextId.value = boundedContexts[0]?.id || null
  }
})
</script>
